import { Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { DateFormatPipe, MomentModule } from 'ngx-moment';
import { ItemDetails } from 'src/app/models/item-details';
import { CustomerNamePipe } from 'src/app/shared/pipes/customer-name.pipe';
import { ConfigService } from 'src/app/shared/services/config.service';

@Component({
  selector: 'app-hires',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    RouterLink,
    MomentModule
  ],
  providers: [DateFormatPipe, CustomerNamePipe],
  template: `
    <div
      class="flex flex-col justify-start items-stretch gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      <div
        class="flex flex-row justify-start items-center gap-x-3 pb-2 border-b border-slate-100"
      >
        <mat-icon class="text-green-500">description</mat-icon>
        <h2 class="text-lg font-bold text-slate-800">Hire Status</h2>
      </div>
      @if (noCurrentHires() === false) {
        <div
          class="flex flex-col gap-2 p-3 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
        >
          <div class="flex flex-row items-center justify-between">
            <h3 class="font-medium text-slate-700">Currently on hire</h3>
            @if (currentHires().length > 1) {
              <span
                class="bg-green-200 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full"
              >
                {{ currentHires().length }} active hires
              </span>
            }
          </div>
          @for (hire of currentHires(); track hire.id) {
            <div
              class="flex flex-row items-center justify-between gap-2 max-w-screen-sm"
            >
              <div class="flex flex-col text-slate-700">
                <p class="font-medium">
                  On hire to {{ hire.customerName }} since
                  {{ hire.onHire | amDateFormat: dateFormat }}
                </p>
              </div>
              <a
                [routerLink]="['/docket', hire.docketId]"
                mat-icon-button
                matTooltip="Go to hire"
                class="text-sm text-green-600 hover:text-green-800"
              >
                <mat-icon>link</mat-icon>
              </a>
            </div>
          }
        </div>
      }

      @if (hasLastHire()) {
        <div class="flex flex-col gap-2 mt-2">
          <div class="flex flex-row items-center justify-between">
            <h3 class="font-medium text-slate-600">
              {{ lastHireTitle() }}
            </h3>
          </div>
          <div
            class="flex flex-row items-center justify-between gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors max-w-screen-sm"
          >
            <div class="flex flex-col text-slate-600">
              <p class="font-medium">
                Hired to {{ lastHire().customerName }}:
                {{ lastHire().onHire | amDateFormat: dateFormat }} -
                {{ lastHire().offHire | amDateFormat: dateFormat }}
              </p>
            </div>
            <a
              [routerLink]="['/docket', lastHire().docketId]"
              mat-icon-button
              matTooltip="Go to hire"
              class="text-sm text-slate-500 hover:text-blue-600"
            >
              <mat-icon>link</mat-icon>
            </a>
          </div>
        </div>
      }

      @if (noHireHistory()) {
        <div class="flex items-center justify-center py-6">
          <p class="text-slate-500 italic flex items-center gap-2">
            <mat-icon class="text-slate-400">info</mat-icon>
            This item has never been hired
          </p>
        </div>
      } @else {
        <button
          mat-stroked-button
          color="primary"
          class="text-sm self-end hover:bg-slate-50 transition-colors mt-2"
          (click)="viewHireHistory()"
        >
          <span class="flex items-center gap-1">
            View Hire History
            <mat-icon class="text-sm">history</mat-icon>
          </span>
        </button>
      }
    </div>
  `
})
export class HiresComponent {
  itemDetails = input.required<ItemDetails>();
  dateFormat = ConfigService.dates.shortDateTimeFormat;
  goToHires = output<void>();

  currentHires = computed(() => this.itemDetails().currentHires || []);
  lastHire = computed(() => this.itemDetails().lastHire);

  noCurrentHires = computed(() => {
    return !this.currentHires() || this.currentHires().length === 0;
  });

  hasLastHire = computed(() => {
    return this.lastHire() !== null && this.lastHire() !== undefined;
  });

  noHireHistory = computed(() => {
    return this.hasLastHire() === false && this.noCurrentHires();
  });

  lastHireTitle = computed(() => {
    if (this.hasLastHire() && this.noCurrentHires()) {
      return 'Not currently on hire, last hire was:';
    }
    return 'Last hire';
  });

  viewHireHistory() {
    this.goToHires.emit();
  }
}
